import { z } from 'zod';
import { currencyForCountry, type Currency } from './enums';
import { BillingCycleSchema, CountrySchema, CurrencySchema, PhoneE164Schema, SkuSchema } from './schemas';
import {
  AsaasWalletIdSchema,
  CommissionWindowMonthsSchema,
  CommissionWindowSchema,
  SplitGatewaySchema,
  SplitPartnerPercentSchema,
  SplitTransferModeSchema,
} from './salesSplit';

/**
 * Parceiro de revenda (#743, F8 da #679 — §4.5, 9.8–9.10, 9.14, 9.15, 9.19).
 *
 * O parceiro vende para os clientes dele; quem paga a licença é o cliente
 * (modelo `DIRECT`, com split para o parceiro) ou o próprio parceiro (modelo
 * `RESELLER`, #965). O modelo "o parceiro cobra e nos repassa"
 * (`commercial_mode: PARTNER`, #743) saiu na #965. No CRM o parceiro é um
 * usuário EXTERNO (`crm_users.role: 'PARTNER'` + `partnerId`), com o
 * perímetro fechado por papel — ver `crm-functions/lib/permissions.ts`.
 *
 * Arquivo próprio, e não `crm.ts`: é um domínio novo, e o `crm.ts` tem mais de
 * 5 mil linhas de outros quatro domínios.
 */

export const CrmPartnerStatusSchema = z.enum(['ACTIVE', 'DISABLED']);
export type CrmPartnerStatus = z.infer<typeof CrmPartnerStatusSchema>;

/**
 * #748 G3 — modelo comercial do parceiro, escolhido no cadastro pelo ADMIN.
 *
 * - `DIRECT`: nós cobramos o CLIENTE pelo gateway do cadastro, e o parceiro
 *   recebe `commissionPercent` do líquido por split (Asaas) ou Connect
 *   (Stripe) — tenant `commercial_mode: DIRECT` + `/salesSplits/{tenantId}`.
 * - `RESELLER` (#965, o default): o PARCEIRO compra a licença de cada cliente
 *   e paga ele mesmo — uma assinatura mensal por tenant, preço fixo
 *   (`pricingFloors.partner_license`), cobrada do parceiro e com a NF em nome
 *   dele (`Subscription.bill_to`). Sem split e sem comissão do parceiro; só a
 *   do gerente dele, pelo extrato a pagar. Tenant `commercial_mode: DIRECT`
 *   (cobrado pelo nosso PSP, passa pelo gate), `pricing_source: PARTNER_LICENSE`.
 *
 * O modelo `PARTNER` (o parceiro cobra o cliente e nos repassa, #743) saiu na
 * #965 (owner, 2026-10-13): a leitura converte o valor gravado em `RESELLER`
 * (`legacyPartnerModel`). Em produção só existiam 2 parceiros de teste nele,
 * sem cliente nem extrato.
 *
 * Trocar o modelo vale só para vendas NOVAS; cliente já vendido muda pelo
 * `changeTenantCommercialMode`.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1 do Asaas; decisão G3 do #748).
 *
 * Usado por: `CrmPartnerSchema.commercialModel` e `CrmUpsertPartnerInput`
 * (este arquivo); `GetSaleCheckoutOptionsOutput.partner.model`
 * (`assistedSale.ts`); CRM — `features/partners/partnerForm.ts` e
 * `PartnerEditorPage.tsx` (seletor do cadastro), `features/checkout/LeadCheckoutPage.tsx`
 * (decide "Ativar cliente" × "Gerar link"), evento `crm_partner_saved`;
 * Functions — `lib/crmCollaboratorGuard.ts` (`requireCrmSaleActor`, doc cru),
 * `billing/getSaleCheckoutOptions.ts`, `billing/provisionTenantForSale.ts`
 * (`partner_model_direct`); crm-functions — `crmPartners.ts` (grava).
 *
 * Falhas conhecidas:
 *  - 2026-09-27 12:34 +0200 (#748, auditoria A1/A4, ALTO): a A4 deixou marcar
 *    um parceiro como DIRECT antes de existir a Contratação DIRECT, e
 *    `runSaleProvision` não lia o modelo — o parceiro ativava sem cobrança.
 *    Hoje recusa com `partner_model_direct`.
 *  - 2026-09-27 15:48 +0200 (#752, A3, B-M5/F-L7): depois da A3 o DIRECT ABRE
 *    cobrança, e o `.catch('DIRECT')` da leitura deixou de ser fechado; os
 *    guards passaram a conferir o doc cru.
 *
 * Issues: #743 (parceiro de revenda), #748 (G3), #750 (A1), #752 (A3), #753 (cadastro),
 * #965 (RESELLER; PARTNER removido).
 *
 * Histórico:
 *  - 2026-10-13 19:09 +0200 — (#965) entra `RESELLER`, sai `PARTNER`.
 */
export const CrmPartnerCommercialModelSchema = z.enum(['DIRECT', 'RESELLER']);
export type CrmPartnerCommercialModel = z.infer<typeof CrmPartnerCommercialModelSchema>;
/**
 * O modelo do cadastro do parceiro como constantes
 * (`CrmPartnerCommercialModel.DIRECT`), derivadas do schema.
 *
 * Origem: #761 — 2026-09-27 17:09 +0200 (o modelo nasceu na A1, #750).
 *
 * Por quê: não confundir com `CommercialMode` (o modo do TENANT, que tem os
 * mesmos textos `PARTNER`/`DIRECT`) nem com `CrmRole.PARTNER` (o papel do
 * usuário). Onde o modelo é lido CRU do Firestore (`requireCrmSaleActor`,
 * `runGetSaleCheckoutOptions`), a constante pega o erro de digitação que o
 * literal deixaria compilar.
 *
 * Contrato: `CrmPartnerCommercialModelSchema.enum`; valores gravados iguais.
 *
 * Usado por: Functions (`crmCollaboratorGuard`, `getSaleCheckoutOptions`,
 * `provisionTenantForSale`), CRM Functions (`crmPartners`) e CRM web
 * (`PartnerEditorPage`, `partnerForm`, `LeadCheckoutPage`).
 *
 * Issues: #761, #750 (A1), #752 (A3).
 */
export const CrmPartnerCommercialModel = CrmPartnerCommercialModelSchema.enum;

/**
 * O valor `'PARTNER'` que o cadastro gravava antes da #965.
 *
 * Origem: #965 — 2026-10-13 19:09 +0200.
 *
 * Por quê: o modelo "o parceiro cobra e nos repassa" saiu, e o que estava nele
 * passa a ser `RESELLER` (decisão do owner). Fica como constante para que os
 * pontos que leem o doc CRU (`requireCrmSaleActor`, `runGetSaleCheckoutOptions`)
 * comparem pelo conceito e não pelo literal (#761).
 *
 * Usado por: `legacyPartnerModel` (este arquivo); Functions
 * `lib/crmCollaboratorGuard.ts` e `billing/getSaleCheckoutOptions.ts`.
 */
export const LEGACY_PARTNER_COMMERCIAL_MODEL = 'PARTNER';

/**
 * Converte o modelo gravado antes da #965 (`PARTNER`) em `RESELLER`; o resto
 * passa sem mudar.
 *
 * Origem: #965 — 2026-10-13 19:09 +0200.
 *
 * Contrato: valor desconhecido segue adiante — quem decide o que fazer com ele
 * é o schema (`.catch(DIRECT)` na leitura, recusa na escrita).
 *
 * Usado por: `CrmPartnerSchema.commercialModel`, `CrmPartnerClientSchema.model`
 * (este arquivo); Functions `crmCollaboratorGuard.ts` (doc cru) e
 * `getSaleCheckoutOptions.ts`.
 */
export function legacyPartnerModel(value: unknown): unknown {
  return value === LEGACY_PARTNER_COMMERCIAL_MODEL ? CrmPartnerCommercialModel.RESELLER : value;
}

/**
 * Endereço fiscal do parceiro (#965) — o tomador da NF no modelo `RESELLER`.
 *
 * Origem: #965 — 2026-10-13 19:09 +0200.
 *
 * Por quê: no RESELLER o cliente do gateway é preenchido com os dados do
 * PARCEIRO, e o Asaas exige endereço estruturado para a NFS-e de boleto/Pix
 * ("Endereço do cliente incompleto; CEP do cliente é inválido", #771). As
 * partes têm os MESMOS nomes do endereço do lead (`CrmLeadSchema`, #771) para
 * `customerAddressFromLead` (Functions) servir aos dois.
 *
 * Dados — `crm_partners/{id}.address`:
 *   { "postalCode": "80240031", "street": "Rua Exemplo", "streetNumber": "100",
 *     "complement": null, "district": "Centro", "city": "Curitiba", "state": "PR" }
 * `postalCode` só dígitos (8 no BR). Fora do BR as partes são livres.
 *
 * Usado por: `CrmPartnerSchema.address`, `CrmUpsertPartnerInput.address`,
 * `crmPartnerFiscalMissing` (este arquivo); CRM `PartnerEditorPage`.
 */
export const CrmPartnerAddressSchema = z.object({
  postalCode: z.string().trim().max(12).nullable(),
  street: z.string().trim().max(160).nullable(),
  streetNumber: z.string().trim().max(20).nullable(),
  complement: z.string().trim().max(80).nullable(),
  district: z.string().trim().max(80).nullable(),
  city: z.string().trim().max(80).nullable(),
  state: z.string().trim().max(40).nullable(),
});
export type CrmPartnerAddress = z.infer<typeof CrmPartnerAddressSchema>;

/**
 * Canais de suporte L1 do parceiro (#968) — o que o cliente dele vê nos apps
 * web, Android e iOS NO LUGAR do suporte do EtiquetaChef.
 *
 * Origem: #968 — 2026-10-14 10:48 +0200 (owner: cliente de parceiro usa o
 * e-mail, o telefone e o WhatsApp do parceiro).
 *
 * Por quê: o parceiro (RESELLER ou DIRECT) atende o primeiro nível dos
 * clientes dele; o cliente não deve cair no nosso suporte. Os três canais são
 * obrigatórios na escrita (owner, 2026-10-14) — o nosso suporte só aparece
 * para um parceiro antigo ainda sem o bloco.
 *
 * Dados — `crm_partners/{id}.supportContact`:
 *   { "phone": "+5541999990000", "whatsapp": "+5541999990000",
 *     "email": "suporte@parceiro.example" }
 * `phone` e `whatsapp` em E.164 (com `+`); os apps tiram o `+` para o `wa.me`.
 * Os dois podem ser o mesmo número. País livre aqui — o formulário trava o DDI
 * do país do parceiro (`PhoneField`).
 *
 * Usado por: `CrmPartnerSchema.supportContact`, `CrmUpsertPartnerInput`
 * (este arquivo); crm-functions `crmUpsertPartner` (grava); functions
 * `getTenantSupportContact` (lê, para web/Android/iOS); CRM `PartnerEditorPage`.
 */
export const CrmPartnerSupportContactSchema = z.object({
  phone: PhoneE164Schema,
  whatsapp: PhoneE164Schema,
  email: z.email().max(254),
});
export type CrmPartnerSupportContact = z.infer<typeof CrmPartnerSupportContactSchema>;

/** Os dados fiscais que um parceiro `RESELLER` pode não ter — chaves do formulário. */
export type CrmPartnerFiscalField =
  | 'legalName'
  | 'billingEmail'
  | 'taxId'
  | 'postalCode'
  | 'street'
  | 'streetNumber'
  | 'district'
  | 'city'
  | 'state';

const BR_PARTNER_ADDRESS_FIELDS = ['postalCode', 'street', 'streetNumber', 'district', 'city', 'state'] as const;

/**
 * O que falta no cadastro de um parceiro `RESELLER` para cobrá-lo e emitir NF
 * em nome dele.
 *
 * Origem: #965 — 2026-10-13 19:09 +0200.
 *
 * Por quê: o parceiro é o pagador e o tomador da NF. Sem razão social, e-mail
 * de cobrança e — no Brasil — documento com 11 ou 14 dígitos e endereço
 * completo, o Asaas recusa o cliente ou a NFS-e só depois do pagamento. Uma
 * regra só, usada no formulário, no `crmUpsertPartner` e na venda.
 *
 * Contrato: `[]` = completo. Fora do BR só razão social e e-mail contam (o
 * Stripe não emite NF). O dígito verificador do CNPJ é conferido no formulário
 * (`TaxIdValidator`); aqui só o comprimento, porque o CPF também é aceito.
 *
 * Usado por: `CrmUpsertPartnerInput` (este arquivo); Functions
 * `requireCrmSaleActor` (`partner_fiscal_incomplete`); CRM `partnerForm.ts`.
 */
export function crmPartnerFiscalMissing(p: {
  readonly countryCode: string;
  readonly taxId: string;
  readonly legalName?: string | null | undefined;
  readonly billingEmail?: string | null | undefined;
  readonly address?: Partial<CrmPartnerAddress> | null | undefined;
}): CrmPartnerFiscalField[] {
  const missing: CrmPartnerFiscalField[] = [];
  const filled = (v: string | null | undefined): boolean => (v ?? '').trim().length > 0;
  if (!filled(p.legalName)) missing.push('legalName');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((p.billingEmail ?? '').trim())) missing.push('billingEmail');
  if (p.countryCode !== 'BR') return missing;
  const digits = p.taxId.replace(/\D/g, '');
  if (digits.length !== 11 && digits.length !== 14) missing.push('taxId');
  const address = p.address ?? {};
  for (const field of BR_PARTNER_ADDRESS_FIELDS) {
    const ok = field === 'postalCode'
      ? (address.postalCode ?? '').replace(/\D/g, '').length === 8
      : filled(address[field]);
    if (!ok) missing.push(field);
  }
  return missing;
}

/**
 * Stripe Connect do parceiro (#757). `acct_…` da conta Express; o status vem
 * do webhook `account.updated`. Só `ACTIVE` recebe por Connect — o resto cai
 * no extrato a pagar.
 *
 * Origem: #750 — 2026-09-27 12:00 +0200 (A1; o campo é reservado para a A8, #757).
 *
 * Usado por: `CrmPartnerSchema.stripeConnect` (este arquivo) e
 * `crmUpsertPartner` (crm-functions), que só PRESERVA o valor. Nada o
 * escreve ainda — o webhook do Connect é da A8 (#757).
 *
 * Dados: `{ "accountId": "acct_000000000000", "status": "PENDING", "updatedAt": 1790000000000 }`.
 */
export const CrmPartnerStripeConnectSchema = z.object({
  /** Id da conta conectada (`acct_…`); o regex barra id de outro tipo de objeto do Stripe. */
  accountId: z.string().regex(/^acct_[A-Za-z0-9]{6,}$/),
  /**
   * `PENDING` = onboarding incompleto; `ACTIVE` = pode receber transfer;
   * `RESTRICTED` = o Stripe suspendeu recebimentos (documento pendente, risco).
   */
  status: z.enum(['PENDING', 'ACTIVE', 'RESTRICTED']),
  /** Epoch ms do último `account.updated` aplicado. */
  updatedAt: z.number().int().nonnegative(),
});
export type CrmPartnerStripeConnect = z.infer<typeof CrmPartnerStripeConnectSchema>;

/** 9.9 — percentual do gerente interno do parceiro sobre o repasse recebido. */
export const CRM_PARTNER_MANAGER_COMMISSION_DEFAULT = 10;
export const CRM_PARTNER_MANAGER_COMMISSION_MIN = 1;
export const CRM_PARTNER_MANAGER_COMMISSION_MAX = 20;
/** Último dia aceito no "Dia do repasse" (#786: era 28). Informativo (9.14). */
export const CRM_PARTNER_REMIT_DAY_MAX = 31;

const ManagerCommissionPercentSchema = z
  .number()
  .min(CRM_PARTNER_MANAGER_COMMISSION_MIN)
  .max(CRM_PARTNER_MANAGER_COMMISSION_MAX);

const PartnerContactSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: z.email().max(254),
  phone: z.string().trim().max(40).nullable(),
});

/**
 * `crm_partners/{partnerId}` — server-only (rules `false`), lido e escrito
 * só pelas callables ADMIN (`crmUpsertPartner`, `crmSetPartnerStatus`).
 *
 * - `countryCode`: o parceiro vende SÓ no próprio país (9.19); a ativação
 *   recusa lead de outro país, e o extrato sai numa moeda só.
 * - `managerUserId`: o MANAGER interno responsável (9.9). É o MANAGER do
 *   quadro pessoal do parceiro (owner, 2026-09-25) e quem recebe a comissão
 *   sobre a parte do EtiquetaChef (DIRECT) ou sobre o líquido que o parceiro
 *   paga (RESELLER, #965 — linha MANUAL, nunca split). **Opcional desde a #786** (owner, 2026-09-30): `null` = parceiro
 *   sem gerente, e aí NÃO há comissão de gerente em caminho nenhum — split
 *   Asaas, transfer Stripe, comissão MANUAL a pagar depois, ou comissão sobre
 *   o repasse. O corte é na origem (`saleSplitInternals`,
 *   `applyPartnerManagerCommission`), nunca no pagamento.
 * - `status: DISABLED` bloqueia só ativação NOVA (9.15): os clientes já
 *   ativados continuam; suspender um cliente é o `suspendTenant` do admin.
 */
export const CrmPartnerSchema = z.object({
  id: z.string().min(1).max(128),
  name: z.string().trim().min(2).max(120),
  countryCode: CountrySchema,
  taxId: z.string().trim().min(3).max(40),
  contact: PartnerContactSchema,
  /**
   * Termos do repasse do modelo `PARTNER`, removido na #965. Só leitura de doc
   * antigo — o `crmUpsertPartner` não o grava mais, e `.catch(null)` faz um
   * doc sem ele (ou malformado) continuar legível. Sai de vez com o código do
   * extrato de repasse (Fase 2 da #965).
   */
  remitTerms: z
    .object({
      /**
       * Dia do mês em que o parceiro costumava repassar — informativo (9.14).
       * #786 (2026-09-30): 1–31, antes 1–28.
       */
      dayOfMonth: z.number().int().min(1).max(CRM_PARTNER_REMIT_DAY_MAX),
      currency: CurrencySchema,
    })
    .nullable()
    .default(null)
    .catch(null),
  status: CrmPartnerStatusSchema,
  /**
   * #786: `null` = sem gerente (sem comissão de gerente). `.default(null)` só
   * cobre doc sem o campo — todo doc anterior à #786 tem string.
   *
   * Deploy: um bundle anterior à #786 (`requireCrmPartner`,
   * `saleSplitSnapshot`, `crmListPartners`) lê `null` como doc inválido e trata
   * o parceiro como inexistente — falha FECHADA (recusa a venda), sem desviar
   * dinheiro. Por isso `crm-functions`, `functions` e `admin` saem juntos, e
   * ninguém grava parceiro sem gerente antes do deploy completo.
   */
  managerUserId: z.string().min(1).max(128).nullable().default(null),
  /** Guardado mesmo sem gerente: é o valor inicial do slider se um for escolhido depois (#786). */
  managerCommissionPercent: ManagerCommissionPercentSchema.default(CRM_PARTNER_MANAGER_COMMISSION_DEFAULT),
  // #748 — leitura TOLERANTE; a escrita estrita é o `CrmUpsertPartnerInput`.
  // Um doc ilegível aqui vira "Parceiro não encontrado" em `requireCrmPartner`
  // e `getCrmPartner`, trancando todos os usuários do parceiro.
  // Auditoria 2026-09-27 12:34 +0200 (#748, A1/A4, MÉDIO): a leitura era
  // estrita e um valor fora do formato nestes campos trancava o parceiro fora
  // do CRM. Causa: `safeParse` do doc inteiro. Não tirar os `.catch` abaixo;
  // o dinheiro é protegido no USO (`asaasDestinationOrNull`,
  // `requireCrmSaleActor` no doc cru — #752, B-M5).
  /**
   * `legacyPartnerModel` + `.default(RESELLER)` (#965): o `PARTNER` gravado
   * antes da #965 — e o doc anterior à #748, sem o campo, que era do modelo de
   * repasse — passa a ser `RESELLER`. `.catch(DIRECT)`: um valor que este
   * bundle não conhece falha FECHADO no que a leitura tolerante decide.
   *
   * Desde a A3 (#752) o DIRECT também ABRE a cobrança pela Contratação, então
   * o `.catch` sozinho não fecha tudo: quem impede um cadastro corrompido de
   * gerar link é `requireCrmSaleActor`
   * (`apps/functions/src/lib/crmCollaboratorGuard.ts`), que relê o doc CRU.
   */
  commercialModel: z.preprocess(
    legacyPartnerModel,
    CrmPartnerCommercialModelSchema.default(CrmPartnerCommercialModel.RESELLER).catch(CrmPartnerCommercialModel.DIRECT),
  ),
  /**
   * Dados fiscais (#965) — o parceiro `RESELLER` é o pagador e o tomador da
   * NF. `legalName` = razão social; `billingEmail` recebe cobrança, NF e os
   * avisos de inadimplência dos clientes dele (`resolveBillingRecipient`).
   * Leitura tolerante como o resto do bloco: `.catch(null)` não tranca o
   * parceiro fora do CRM; quem exige os dados é `crmPartnerFiscalMissing`, no
   * cadastro e na venda.
   */
  legalName: z.string().max(160).nullable().default(null).catch(null),
  billingEmail: z.string().max(254).nullable().default(null).catch(null),
  address: CrmPartnerAddressSchema.nullable().default(null).catch(null),
  /**
   * Suporte L1 (#968). `null` = parceiro anterior à #968, ainda sem o bloco —
   * o cliente dele continua vendo o suporte do EtiquetaChef até o ADMIN
   * salvar o cadastro (o `crmUpsertPartner` exige o bloco). `.catch(null)`:
   * mesma política do bloco tolerante — um valor malformado não tranca o
   * parceiro fora do CRM, só devolve o suporte ao EtiquetaChef.
   */
  supportContact: CrmPartnerSupportContactSchema.nullable().default(null).catch(null),
  /** Inscrição municipal/estadual — opcionais, vão ao cliente do Asaas quando houver. */
  municipalInscription: z.string().max(40).nullable().default(null).catch(null),
  stateInscription: z.string().max(40).nullable().default(null).catch(null),
  /**
   * Só no DIRECT: gateway das vendas deste parceiro — a Contratação o fixa,
   * sem troca (`resolveSaleGateway`, #752). `.catch(null)`: valor corrompido
   * lê como "sem gateway", e o parceiro sem gateway válido no doc CRU é
   * recusado por `requireCrmSaleActor` (`partner_gateway_missing`, auditoria
   * da A3) — a tolerância da leitura não abre venda por um gateway inventado.
   */
  paymentGateway: SplitGatewaySchema.nullable().optional().catch(null),
  /**
   * Só no DIRECT: percentual do parceiro sobre o líquido (0–50 na escrita).
   * A leitura é mais larga (até 100), como todo este bloco tolerante: um
   * valor fora do teto de escrita não pode tornar o doc ilegível. O dinheiro
   * continua protegido no uso — o `superRefine` de `SalesSplitSchema` barra
   * soma acima de 100 %.
   */
  commissionPercent: z.number().min(0).max(100).nullable().optional().catch(null),
  /** Carteira Asaas do split — formato conferido no uso (`asaasDestinationOrNull`). */
  asaasWalletId: z.string().max(128).nullable().optional().catch(null),
  /**
   * Conta Connect (#757). Gravada só pelo webhook do Connect — o
   * `crmUpsertPartner` preserva o valor existente, nunca o recebe do
   * formulário. Até a A8 a venda Stripe paga o parceiro pelo extrato.
   */
  stripeConnect: CrmPartnerStripeConnectSchema.nullable().optional().catch(null),
  /**
   * Prazo da comissão, em meses, contado do 1º pagamento (split/Stripe) ou do
   * 1º repasse do cliente (modelo PARTNER); `null` = tempo indeterminado.
   * Vale para o parceiro E para o gerente dele nas vendas deste parceiro
   * (#780, decisão 6 do owner, 2026-09-29 12:48 +0200).
   *
   * `.default(null)`: todo parceiro anterior à #780 recebia sem prazo.
   * `.catch(null)`: mesma política dos campos acima — valor ilegível não
   * tranca o parceiro fora do CRM; `partnerCommissionWindowOf` repete a regra
   * no uso, sobre o doc cru.
   */
  commissionWindowMonths: CommissionWindowMonthsSchema.nullable().default(null).catch(null),
  notes: z.string().max(2000).nullable().optional(),
  createdAt: z.number().int().nonnegative(),
  createdBy: z.string().min(1),
  updatedAt: z.number().int().nonnegative(),
  updatedBy: z.string().min(1),
});
export type CrmPartner = z.infer<typeof CrmPartnerSchema>;

/** A moeda do repasse é a do país do parceiro (9.19) — fonte única. */
export function crmPartnerCurrencyFor(countryCode: string): Currency {
  return currencyForCountry(countryCode);
}

// ---------------------------------------------------------------------------
// Callables ADMIN — cadastro
// ---------------------------------------------------------------------------

export const CrmUpsertPartnerInput = z.object({
  /** Ausente = parceiro novo. */
  partnerId: z.string().min(1).max(128).optional(),
  name: z.string().trim().min(2).max(120),
  countryCode: CountrySchema,
  taxId: z.string().trim().min(3).max(40),
  contact: PartnerContactSchema,
  /**
   * Dia do repasse do modelo `PARTNER`, removido na #965. Aceito e IGNORADO:
   * o formulário anterior à #965 ainda o manda na janela de deploy.
   */
  remitDayOfMonth: z.number().int().min(1).max(CRM_PARTNER_REMIT_DAY_MAX).optional(),
  /** #786: `null` = parceiro sem gerente interno, e portanto sem comissão de gerente. */
  managerUserId: z.string().min(1).max(128).nullable(),
  managerCommissionPercent: ManagerCommissionPercentSchema.default(CRM_PARTNER_MANAGER_COMMISSION_DEFAULT),
  /**
   * #748 G3 / #965. Ausente = `RESELLER`, o default do cadastro desde a #965.
   * `PARTNER` (formulário anterior à #965) é RECUSADO, e não convertido: na
   * escrita, gravar RESELLER sem os dados fiscais seria um cadastro que não
   * vende — melhor o ADMIN ver o erro e recarregar a tela.
   */
  commercialModel: CrmPartnerCommercialModelSchema.default(CrmPartnerCommercialModel.RESELLER),
  /**
   * #965 — dados fiscais; exigidos no RESELLER (`crmPartnerFiscalMissing`, no
   * `superRefine`). AUSENTE (`undefined`) = formulário anterior à #965: o
   * `crmUpsertPartner` PRESERVA o gravado em vez de apagar (auditoria
   * 2026-10-13, #965 BAIXO-6 — uma aba antiga salvando um DIRECT zerava os
   * dados fiscais). `null` = o ADMIN limpou o campo.
   */
  legalName: z.string().trim().max(160).nullable().optional(),
  billingEmail: z.email().max(254).nullable().optional(),
  address: CrmPartnerAddressSchema.nullable().optional(),
  municipalInscription: z.string().trim().max(40).nullable().optional(),
  stateInscription: z.string().trim().max(40).nullable().optional(),
  /**
   * #968 — suporte L1, obrigatório em TODO modelo (owner, 2026-10-14).
   * Diferente dos dados fiscais, não há "ausente = preserva": uma aba anterior
   * à #968 é recusada e o ADMIN recarrega — o mesmo critério do `PARTNER`
   * recusado acima.
   */
  supportContact: CrmPartnerSupportContactSchema,
  /** Obrigatório no DIRECT (`superRefine`); o servidor grava `null` no RESELLER. */
  paymentGateway: SplitGatewaySchema.nullable().default(null),
  /** Obrigatório no DIRECT: 0–50, até 2 casas (`SplitPartnerPercentSchema`). */
  commissionPercent: SplitPartnerPercentSchema.nullable().default(null),
  /**
   * Estrita na escrita (UUID do Asaas), ao contrário da leitura tolerante:
   * o formulário não grava carteira malformada. A EXISTÊNCIA da carteira não
   * é provada aqui — só uma cobrança-sonda prova (docs/billing/asaas.md,
   * "Carteira do split", #753).
   */
  asaasWalletId: AsaasWalletIdSchema.nullable().default(null),
  /**
   * Prazo da comissão (#780): 1–120 meses ou `null` = indeterminado.
   * Ausente = formulário anterior à #780 — o `crmUpsertPartner` preserva o
   * valor gravado (ou `null` no parceiro novo), em vez de zerar um prazo que
   * a tela antiga nem mostrava.
   */
  commissionWindowMonths: CommissionWindowSchema.optional(),
  notes: z.string().max(2000).nullable().default(null),
}).superRefine((input, ctx) => {
  // As exigências dependem do modelo, e por isso moram num refine e não nos
  // campos: RESELLER precisa dos dados fiscais (#965); DIRECT, de gateway e
  // percentual. Campos do outro modelo são aceitos e ignorados pelo servidor.
  switch (input.commercialModel) {
    case CrmPartnerCommercialModel.RESELLER: {
      for (const field of crmPartnerFiscalMissing(input)) {
        const path = field === 'legalName' || field === 'billingEmail' || field === 'taxId' ? [field] : ['address', field];
        ctx.addIssue({ code: 'custom', path, message: 'Parceiro que compra a licença exige os dados fiscais.' });
      }
      return;
    }
    case CrmPartnerCommercialModel.DIRECT: {
      if (input.paymentGateway === null) {
        ctx.addIssue({ code: 'custom', path: ['paymentGateway'], message: 'Parceiro DIRECT exige o gateway.' });
      }
      if (input.commissionPercent === null) {
        ctx.addIssue({ code: 'custom', path: ['commissionPercent'], message: 'Parceiro DIRECT exige o percentual de comissão.' });
      }
      // Sem carteira a venda NÃO é bloqueada (a comissão vai para o extrato a
      // pagar) — mas cadastrar Asaas com percentual e sem carteira é quase
      // sempre esquecimento; a tela avisa, o servidor aceita.
      return;
    }
    default: {
      const exhaustive: never = input.commercialModel;
      return exhaustive;
    }
  }
});
export type CrmUpsertPartnerInput = z.infer<typeof CrmUpsertPartnerInput>;

export const CrmSetPartnerStatusInput = z.object({
  partnerId: z.string().min(1).max(128),
  status: CrmPartnerStatusSchema,
});
export type CrmSetPartnerStatusInput = z.infer<typeof CrmSetPartnerStatusInput>;

/**
 * Input de `crmDeletePartner` (#786). O servidor recusa parceiro com cliente,
 * atual ou antigo. `disableUsers`: desativa, no mesmo batch, os usuários do
 * CRM ligados ao parceiro — o ADMIN escolhe no diálogo, que os lista.
 */
export const CrmDeletePartnerInput = z.object({
  // Sem barra: `doc("a/b")` resolveria um subdocumento e lançaria erro
  // interno em vez de `not-found` (auditoria #786, B3).
  partnerId: z.string().min(1).max(128).regex(/^[^/]+$/),
  disableUsers: z.boolean(),
});
export type CrmDeletePartnerInput = z.infer<typeof CrmDeletePartnerInput>;

/** Linha da lista de parceiros (ADMIN). */
export const CrmPartnerListItemSchema = CrmPartnerSchema.extend({
  managerName: z.string().nullable(),
  /** Usuários do CRM com papel PARTNER vinculados a este parceiro. */
  userCount: z.number().int().nonnegative(),
  /** Tenants com `partner_id` = este parceiro. */
  clientCount: z.number().int().nonnegative(),
  /**
   * #786: `tenants/{id}.createdAt` (epoch ms) do cliente mais recente deste
   * parceiro — só os atuais (`partner_id`), como `clientCount`; `null` = sem
   * cliente. É a data de CRIAÇÃO do tenant: um cliente que veio de venda
   * direta para o parceiro mostra a data antiga. Tenant legado com `createdAt`
   * Timestamp conta em `clientCount` mas não entra na data.
   */
  lastClientAt: z.number().int().nonnegative().nullable(),
});
export type CrmPartnerListItem = z.infer<typeof CrmPartnerListItemSchema>;

// ---------------------------------------------------------------------------
// Extrato de repasse — `crm_partner_remittances/{partnerId}_{YYYY-MM}`
// ---------------------------------------------------------------------------

/** Período do extrato: mês de calendário em `America/Sao_Paulo`. */
export const CrmRemittancePeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
export type CrmRemittancePeriod = z.infer<typeof CrmRemittancePeriodSchema>;

export function crmPartnerRemittanceId(partnerId: string, period: string): string {
  return `${partnerId}_${period}`;
}

/**
 * Por que a linha está no extrato do mês (9.8):
 * - `MONTHLY`: item mensal ativo no mês — entra todo mês;
 * - `ACTIVATION`: item não mensal no mês em que foi ativado — entra integral;
 * - `RENEWAL`: item não mensal no mês de cada aniversário do ciclo.
 */
export const CrmRemittanceLineReasonSchema = z.enum(['MONTHLY', 'ACTIVATION', 'RENEWAL']);
export type CrmRemittanceLineReason = z.infer<typeof CrmRemittanceLineReasonSchema>;

export const CrmRemittanceLineSchema = z.object({
  tenantId: z.string().min(1),
  tenantName: z.string(),
  sku: SkuSchema,
  billingCycle: BillingCycleSchema,
  remitCents: z.number().int().nonnegative(),
  reason: CrmRemittanceLineReasonSchema,
  /**
   * Período (YYYY-MM) do 1º extrato deste parceiro que trouxe este cliente —
   * início do prazo da comissão do gerente sobre o repasse (#780, decisão 7 do
   * owner, 2026-09-29 12:48 +0200). Congelado na geração do extrato.
   * Ausente = extrato anterior à #780: a linha conta como dentro do prazo.
   */
  firstRemitPeriod: CrmRemittancePeriodSchema.optional(),
});
export type CrmRemittanceLine = z.infer<typeof CrmRemittanceLineSchema>;

/**
 * `PENDING` → o ADMIN marca `RECEIVED` (com data e observação, 9.14) ou
 * `DISPUTED`. Só o `RECEIVED` gera a comissão do gerente (9.9); voltar dele
 * cancela a comissão ainda não paga e é recusado se ela já foi paga.
 */
export const CrmRemittanceStatusSchema = z.enum(['PENDING', 'RECEIVED', 'DISPUTED']);
export type CrmRemittanceStatus = z.infer<typeof CrmRemittanceStatusSchema>;

export const CrmPartnerRemittanceSchema = z.object({
  id: z.string().min(1),
  partnerId: z.string().min(1),
  period: CrmRemittancePeriodSchema,
  currency: CurrencySchema,
  lines: z.array(CrmRemittanceLineSchema),
  totalRemitCents: z.number().int().nonnegative(),
  status: CrmRemittanceStatusSchema,
  /**
   * Congelados na GERAÇÃO: trocar o gerente depois não muda o extrato.
   * #786: `null` = parceiro sem gerente naquele mês — o `RECEIVED` não gera
   * comissão de gerente (`applyPartnerManagerCommission`).
   */
  managerUserId: z.string().min(1).nullable(),
  /**
   * #788 (2026-09-30): o gerente deste extrato saiu de cena (rebaixado,
   * desativado ou removido) antes do RECEIVED — `managerUserId` fica `null` e
   * o recálculo (`writePartnerRemittance`) NÃO o repõe com o gerente atual do
   * parceiro: a parte dele não vai para ninguém (decisão do owner). Ausente =
   * `false`.
   */
  managerRevoked: z.boolean().optional(),
  managerCommissionPercent: z.number().min(0).max(100),
  generatedAt: z.number().int().nonnegative(),
  /** `SCHEDULE` (job do dia 1) ou o uid do ADMIN que recalculou. */
  generatedBy: z.string().min(1),
  receivedAt: z.number().int().nonnegative().nullable(),
  statusChangedBy: z.string().min(1).nullable(),
  statusChangedAt: z.number().int().nonnegative().nullable(),
  note: z.string().max(1000).nullable(),
  /** Id da comissão gerada no `RECEIVED` (`{remittanceId}_{managerUid}`). */
  commissionId: z.string().min(1).nullable(),
});
export type CrmPartnerRemittance = z.infer<typeof CrmPartnerRemittanceSchema>;

export const CrmListPartnerRemittancesInput = z.object({
  /** ADMIN: obrigatório. PARCEIRO: ignorado — é sempre o dele. */
  partnerId: z.string().min(1).max(128).optional(),
  limit: z.number().int().min(1).max(36).default(12),
});
export type CrmListPartnerRemittancesInput = z.infer<typeof CrmListPartnerRemittancesInput>;

export const CrmRecomputePartnerRemittanceInput = z.object({
  partnerId: z.string().min(1).max(128),
  period: CrmRemittancePeriodSchema,
});
export type CrmRecomputePartnerRemittanceInput = z.infer<typeof CrmRecomputePartnerRemittanceInput>;

export const CrmSetPartnerRemittanceStatusInput = z
  .object({
    remittanceId: z.string().min(1).max(160),
    status: CrmRemittanceStatusSchema,
    /** Data em que o dinheiro chegou (epoch ms). Exigida no `RECEIVED`. */
    receivedAt: z.number().int().nonnegative().nullable().default(null),
    note: z.string().trim().max(1000).nullable().default(null),
  })
  .refine((v) => v.status !== 'RECEIVED' || v.receivedAt !== null, {
    message: 'informe a data do recebimento',
    path: ['receivedAt'],
  });
export type CrmSetPartnerRemittanceStatusInput = z.infer<typeof CrmSetPartnerRemittanceStatusInput>;

// ---------------------------------------------------------------------------
// Extrato de comissões do parceiro DIRECT — `crm_commissions` (#753, A4)
// ---------------------------------------------------------------------------

/**
 * Entrada de `crmListPartnerCommissions`.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200 (A4; decisão do owner no mesmo dia:
 * as linhas do parceiro DIRECT vivem em `crm_commissions`, não no doc mensal de
 * repasse, porque o status daquele doc é o do dinheiro que ENTRA).
 *
 * Contrato: igual ao `CrmListPartnerRemittancesInput` — ADMIN informa o
 * parceiro; o PARCEIRO vê sempre o próprio (`resolvePartnerScope`). `months`
 * conta meses de calendário de `America/Sao_Paulo`, o atual incluído.
 */
export const CrmListPartnerCommissionsInput = z.object({
  /** ADMIN: obrigatório. PARCEIRO: ignorado — é sempre o dele. */
  partnerId: z.string().min(1).max(128).optional(),
  months: z.number().int().min(1).max(36).default(12),
});
export type CrmListPartnerCommissionsInput = z.infer<typeof CrmListPartnerCommissionsInput>;

/**
 * Uma linha do extrato de comissões: um pagamento de um cliente do parceiro.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Contrato: projeção de `CrmCommission` (linha `PARTNER`) + o nome do tenant.
 * Não leva nada de colaborador interno — o parceiro é usuário externo.
 */
export const CrmPartnerCommissionLineSchema = z.object({
  id: z.string().min(1),
  tenantId: z.string(),
  tenantName: z.string(),
  paymentId: z.string(),
  /** Quando o cliente pagou (epoch ms). */
  paidStripeAt: z.number().int(),
  /** Líquido do pagamento, na moeda da linha. */
  baseAmountCents: z.number().int().nonnegative(),
  netPercent: z.number().min(0).max(100).nullable(),
  amountCents: z.number().int().nonnegative(),
  currency: z.string(),
  paidVia: SplitTransferModeSchema.nullable(),
  status: z.enum(['PENDING', 'PAID', 'CANCELLED']),
  splitStatus: z.string().nullable(),
  /** Quando o ADMIN pagou a linha `MANUAL` (ou o cliente pagou, nas do gateway). */
  paidAt: z.number().int().nullable(),
});
export type CrmPartnerCommissionLine = z.infer<typeof CrmPartnerCommissionLineSchema>;

/**
 * Um mês do extrato de comissões, numa moeda — totais separados por natureza.
 *
 * Origem: #753 — 2026-09-27 19:03 +0200.
 *
 * Por quê: "paga por split" e "paga por Connect" são INFORMATIVAS (o dinheiro
 * já está com o parceiro); só "a pagar" é dívida nossa. Somá-las num total só
 * faria o parceiro cobrar o que já recebeu. Linhas `CANCELLED` (estorno) ficam
 * na lista e fora de todos os totais.
 */
export const CrmPartnerCommissionMonthSchema = z.object({
  period: CrmRemittancePeriodSchema,
  currency: z.string(),
  splitCents: z.number().int().nonnegative(),
  connectCents: z.number().int().nonnegative(),
  payablePendingCents: z.number().int().nonnegative(),
  payablePaidCents: z.number().int().nonnegative(),
  lines: z.array(CrmPartnerCommissionLineSchema),
});
export type CrmPartnerCommissionMonth = z.infer<typeof CrmPartnerCommissionMonthSchema>;

export const CrmListPartnerCommissionsOutput = z.object({
  partnerId: z.string().min(1),
  months: z.array(CrmPartnerCommissionMonthSchema),
});
export type CrmListPartnerCommissionsOutput = z.infer<typeof CrmListPartnerCommissionsOutput>;

// ---------------------------------------------------------------------------
// Portal do parceiro — `/partner/clients`
// ---------------------------------------------------------------------------

export const CrmPartnerClientItemSchema = z.object({
  sku: SkuSchema,
  amountCents: z.number().int().nonnegative(),
  remitCents: z.number().int().nonnegative(),
});

/**
 * O cliente como o parceiro o vê: cadastro comercial e composição. Nada
 * operacional — o parceiro não é membro do tenant (9.15).
 */
export const CrmPartnerClientSchema = z.object({
  tenantId: z.string(),
  name: z.string(),
  /** `ACTIVE`, `SUSPENDED` ou `CANCELLED`, pelo status do tenant/assinatura. */
  status: z.enum(['ACTIVE', 'SUSPENDED', 'CANCELLED']),
  billingCycle: BillingCycleSchema,
  currency: CurrencySchema,
  items: z.array(CrmPartnerClientItemSchema),
  totalAmountCents: z.number().int().nonnegative(),
  totalRemitCents: z.number().int().nonnegative(),
  activatedAt: z.number().int().nonnegative(),
  boardId: z.string().nullable(),
  leadId: z.string().nullable(),
  /**
   * A tela Assinatura (pós-venda) aceita este cliente? `true` só quando o lead
   * de origem está `WON` e vinculado a ESTE tenant — a mesma condição com que
   * `requirePostSaleContext`/`partnerPostSale` recusam `lead_not_won`.
   *
   * Por quê (#743, 2026-09-28 16:54 +0200): a lista traz todo tenant com `partner_id`, inclusive o
   * provisionado cujo link ainda não foi pago (lead `OPEN`). O botão
   * "Assinatura" levava a uma tela que só dizia "O pós-venda só existe para um
   * lead ganho com cliente vinculado". O servidor decide, a tela só esconde.
   * `.default(false)`: resposta de um deploy anterior (sem o campo) esconde o
   * botão em vez de oferecer a tela que recusa.
   */
  postSaleAvailable: z.boolean().default(false),
  /**
   * Quem cobra este cliente (#743/#752, 2026-09-28 17:04 +0200). `PARTNER`: o parceiro cobra e
   * nos repassa — `totalAmountCents`/`totalRemitCents` são os itens de origem
   * `PARTNER`. `DIRECT`: nós cobramos pelo gateway e o parceiro recebe comissão
   * — `totalAmountCents` soma os itens pagos (fora cortesia `GRANT`),
   * `totalRemitCents` é 0 e a comissão vai em `commissionPercent`.
   *
   * Por quê: a lista nasceu só para o modelo PARTNER (F8) e somava só itens
   * `PARTNER`; um cliente DIRECT (itens `GATEWAY`) aparecia com "Cobrado R$ 0,00".
   *
   * #965 (2026-10-13 19:09 +0200): `RESELLER` = nós cobramos o PARCEIRO
   * (`Subscription.bill_to`); `totalAmountCents` soma os itens pagos,
   * `totalRemitCents` é 0 e não há comissão do parceiro. O `PARTNER` que um
   * deploy anterior ainda devolva lê como `RESELLER` (`legacyPartnerModel`).
   */
  model: z.preprocess(
    legacyPartnerModel,
    CrmPartnerCommercialModelSchema.default(CrmPartnerCommercialModel.RESELLER),
  ),
  /**
   * DIRECT: a comissão do parceiro CONGELADA na venda (`net_percent` do
   * beneficiário `PARTNER` em `/salesSplits/{tenantId}`), em %. É percentual e
   * não valor porque o Asaas aplica o split sobre o LÍQUIDO (valor menos as
   * taxas da cobrança), que só existe depois do pagamento. `null` no PARTNER e
   * quando não há split congelado.
   */
  commissionPercent: z.number().min(0).max(100).nullable().default(null),
  /**
   * DIRECT: quanto o parceiro RECEBEU no último pagamento deste cliente, em
   * centavos — o split para a carteira dele em `/paymentSettlements`. `null`
   * antes do 1º pagamento (ou sem carteira: Stripe/extrato manual).
   *
   * Origem: #743 — 2026-09-29 08:04 +0200 (owner: "Comissão de 50%" levava o parceiro a
   * pensar em 50% de R$ 550, quando é 50% do LÍQUIDO; melhor mostrar o valor).
   */
  lastCommissionCents: z.number().int().nonnegative().nullable().default(null),
});
export type CrmPartnerClient = z.infer<typeof CrmPartnerClientSchema>;

export const CrmPartnerListClientsInput = z.object({
  /** ADMIN: obrigatório. PARCEIRO: ignorado — é sempre o dele. */
  partnerId: z.string().min(1).max(128).optional(),
});
export type CrmPartnerListClientsInput = z.infer<typeof CrmPartnerListClientsInput>;

/**
 * O que o PARCEIRO logado pode saber do próprio cadastro (`crmPartnerGetMe`).
 *
 * Origem: #769 — 2026-09-28 17:24 +0200.
 *
 * Por quê: `crm_partners` só é lido por super-admin (rules), e o editor de lead
 * precisa do país do parceiro para travar o país do lead e mascarar telefone e
 * documento (+55 fixo no BR). Expõe o mínimo — nada de comissão, carteira ou
 * gerente, que continuam só do admin.
 *
 * Dados — saída da callable:
 *   { "partnerId": "p-000", "countryCode": "BR" }
 *
 * Usado por: callable `crmPartnerGetMe` (crm-functions, escreve);
 * `useMyPartner` → `LeadEditorPage` (CRM web, lê).
 */
export const CrmPartnerGetMeInput = z.object({}).strict();
export type CrmPartnerGetMeInput = z.infer<typeof CrmPartnerGetMeInput>;
export const CrmPartnerMeSchema = z.object({
  partnerId: z.string().min(1),
  countryCode: CountrySchema,
});
export type CrmPartnerMe = z.infer<typeof CrmPartnerMeSchema>;
